import dayjs from "dayjs";
import firebase from "firebase/compat/app";
import { Booking, Campaign, Client, List, ListAddCountsByMonth, Pitch, Result, Talent, User } from '../../shared/types';
import { pitchDigestFields, type PitchDigestAction } from "../../utils/pitching/digest";
import { analyticsService } from "../analytics";
import { captureFirebaseErrorForSentry } from "../sentryCapture";
import { firebaseConfig, firestore, logDatabaseReads } from "./app";
import { uploadFile } from "./storage";

////////////////////////////////
// Types and Helpers
////////////////////////////////

export type FirestoreLeaf =
    | string
    | number
    | boolean
    | null
    | Date
    | firebase.firestore.Timestamp
    | firebase.firestore.FieldValue
    | firebase.firestore.GeoPoint
    | firebase.firestore.DocumentReference;

export type FirestoreData = {
    readonly [key: string]: FirestoreLeaf | FirestoreData | readonly FirestoreLeaf[] | readonly FirestoreData[];
};

/** One Firestore field value. Present value cannot be `undefined`. */
export type FirestoreField = FirestoreData[string];

type DeepNullable<T> = {
    [K in keyof T]: NullableValue<T[K]>;
};

type NullableValue<V> =
    [NonNullable<V>] extends [FirestoreLeaf] ? V | null
    : [NonNullable<V>] extends [readonly (infer U)[]] ? ReadonlyArray<NullableValue<U>> | null
    : [NonNullable<V>] extends [object] ? DeepNullable<NonNullable<V>> | null
    : V | null;

type DeepNoUndefined<T> = {
    [K in keyof T]: undefined extends T[K] ? never
    : null extends T[K] ? NoUndefinedValue<Exclude<T[K], null>> | null
    : NoUndefinedValue<T[K]>;
};

type NoUndefinedValue<V> =
    [V] extends [FirestoreLeaf] ? V
    : [V] extends [readonly (infer U)[]] ? Array<NoUndefinedValue<U>>
    : [V] extends [object] ? DeepNoUndefined<V>
    : V;

type NestedEmptyToNull<T> = {
    [K in keyof T]-?: NestedEmptyToNullValue<T[K]>;
};

type NestedEmptyToNullValue<V> =
    [NonNullable<V>] extends [FirestoreLeaf] ? Exclude<V, undefined | ""> | null
    : [NonNullable<V>] extends [readonly (infer U)[]] ? Array<NestedEmptyToNullValue<U>> | (undefined extends V ? null : never)
    : [NonNullable<V>] extends [object] ? NestedEmptyToNull<NonNullable<V>> | (undefined extends V ? null : never)
    : Exclude<V, undefined> | null;

/**
 * Write-payload certificate: included keys cannot be `undefined`. Does not require `id`.
 * @see .cursor/rules/firestore-writes.mdc
 * @see docs/firestore-writes.md
 */
export function createUpdate<Model>() {
    return <const T extends Partial<DeepNullable<Model>>>(data: T & DeepNoUndefined<T>): T => data;
}

/** `users` write payload. @see .cursor/rules/firestore-writes.mdc @see docs/firestore-writes.md */
export const createUserUpdate = createUpdate<User>();
/** `campaigns` write payload. @see .cursor/rules/firestore-writes.mdc @see docs/firestore-writes.md */
export const createCampaignUpdate = createUpdate<Campaign>();
/** `clients` write payload. @see .cursor/rules/firestore-writes.mdc @see docs/firestore-writes.md */
export const createClientUpdate = createUpdate<Client>();
/** `lists` write payload. @see .cursor/rules/firestore-writes.mdc @see docs/firestore-writes.md */
export const createListUpdate = createUpdate<List>();
/** `talent` write payload (`Talent`, not `User`). @see .cursor/rules/firestore-writes.mdc @see docs/firestore-writes.md */
export const createTalentUpdate = createUpdate<Talent>();
/** `bookings` write payload. Written via `updateDoc`. @see .cursor/rules/firestore-writes.mdc @see docs/firestore-writes.md */
export const createBookingUpdate = createUpdate<Booking>();

/**
 * Leaf empty policy: `undefined` and `""` become `null`. Does not walk objects.
 * Arrays of leaves: `tags.map(convertEmptyToNull)`.
 * @see .cursor/rules/firestore-writes.mdc
 * @see docs/firestore-writes.md
 */
export function convertEmptyToNull<T extends FirestoreLeaf | undefined>(value: T): Exclude<T, undefined | ""> | null {
    if (value === undefined || value === "") {
        return null;
    }
    return value as Exclude<T, undefined | "">;
}

/**
 * Same empty policy as {@link convertEmptyToNull}, recursing through maps and arrays.
 * Nested means the walk, not empty object → null. For a form-owned object after stripping
 * non-persisted keys (`clientObj`, `file`, `bookings`, `docMeta`).
 * @see .cursor/rules/firestore-writes.mdc
 * @see docs/firestore-writes.md
 */
export function convertNestedEmptyToNull<T extends object>(value: T): NestedEmptyToNull<T> {
    return convertNestedEmptyToNullValue(value) as NestedEmptyToNull<T>;
}

function isSentinel(value: object): boolean {
    return value instanceof Date
        || value instanceof firebase.firestore.Timestamp
        || value instanceof firebase.firestore.FieldValue
        || value instanceof firebase.firestore.GeoPoint
        || value instanceof firebase.firestore.DocumentReference
        || typeof (value as { toDate?: unknown }).toDate === "function";
}

function convertNestedEmptyToNullValue(value: unknown): unknown {
    if (value === undefined || value === "") {
        return null;
    }
    if (value === null || typeof value !== "object") {
        return value;
    }
    if (isSentinel(value)) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(convertNestedEmptyToNullValue);
    }
    return Object.fromEntries(
        Object.entries(value).map(([key, item]) => [key, convertNestedEmptyToNullValue(item)]),
    );
}

function isDroppedEmptyMap(value: unknown): boolean {
    return typeof value === "object"
        && value !== null
        && !Array.isArray(value)
        && !isSentinel(value)
        && Object.keys(value).length === 0;
}

function omitUndefinedValue(value: unknown): unknown {
    if (value === null || typeof value !== "object") {
        return value;
    }
    if (isSentinel(value)) {
        return value;
    }
    if (Array.isArray(value)) {
        const next: unknown[] = [];
        for (const item of value) {
            if (item === undefined) {
                continue;
            }
            const walked = omitUndefinedValue(item);
            if (isDroppedEmptyMap(walked)) {
                continue;
            }
            next.push(walked);
        }
        return next;
    }
    const next: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
        if (item === undefined) {
            continue;
        }
        const walked = omitUndefinedValue(item);
        if (isDroppedEmptyMap(walked)) {
            continue;
        }
        next[key] = walked;
    }
    return next;
}

/**
 * Drops `undefined` keys, `undefined` array items, and nested `{}` after the walk.
 * Preserves Date / Timestamp / FieldValue / GeoPoint / DocumentReference.
 * Spreading a full `User` into `createUserUpdate` still fails; spread a picked object
 * (`omitUndefined({ phone: user.phone })`).
 * @see .cursor/rules/firestore-writes.mdc
 * @see docs/firestore-writes.md
 */
export function omitUndefined<T extends object>(value: T): { [K in keyof T]: Exclude<T[K], undefined> } {
    return omitUndefinedValue(value) as { [K in keyof T]: Exclude<T[K], undefined> };
}

export function generateNewDocId(collection: string) {
    const newDocRef = firestore.collection(collection).doc();
    return newDocRef.id;
}

////////////////////////////////
// Feature Writes
////////////////////////////////

export async function addNewUserInfo<const T extends Partial<DeepNullable<User>> & DeepNoUndefined<T>>(uid: string, newUserInfo: T) {
    const userRef = firestore.collection("users").doc(uid);
    const res = await userRef.set({
        ...newUserInfo,
        docMeta: {
            ...("docMeta" in newUserInfo && newUserInfo.docMeta && typeof newUserInfo.docMeta === "object" ? newUserInfo.docMeta : {}),
            updateTime: firebase.firestore.FieldValue.serverTimestamp(),
            lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
            lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp(),
        },
    }, { merge: true });

    console.log(`addNewUserInfo res`, res);
}

export async function createUser<const T extends Partial<DeepNullable<User>> & DeepNoUndefined<T>>(userData: T) {
    let result: Result;

    try {
        // Create secondary app to avoid loging in and kickout out existing user...
        // https://stackoverflow.com/a/38013551/2972273
        const secondaryApp = firebase.initializeApp(firebaseConfig, `Secondary-${new Date().getTime().toString()}`);
        const authRes = await secondaryApp.auth().createUserWithEmailAndPassword(userData.email!, new Date().getTime().toString());

        const uid = authRes.user!.uid;
        await addNewUserInfo(uid, userData);
        await addNewUserInfo(uid, createUserUpdate({
            orgDomain: userData.email!.split('@')[1]?.toLowerCase() || null,
            docMeta: {
                createTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
                updateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
                lastSignInTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
                lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
                lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
            },
        }));

        result = { success: true, result: authRes.user!.uid };
        secondaryApp.delete().then(function () {
            console.log("DELETED APP");
        });

        return result;
    } catch (e) {
        result = { success: false, error: e };
        console.log(e);
        analyticsService().logError(e);
        captureFirebaseErrorForSentry(e, 'createUser');
        return result;
    }
}

export async function createClient(clientData: Client) {
    let result: Result;

    clientData.docMeta = {
        updateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
        lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
        lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
    };

    // we always want new clients to get invoices at both emails by default
    clientData.invoiceBothBookerAndClient = true;

    try {
        const clientRef = firestore.collection('clients');
        const clientRes = await clientRef.add(clientData);

        return {
            success: true,
            result: clientRes.id
        }

    } catch (e) {
        result = { success: false, error: e };
        console.log(e);
        analyticsService().logError(e);
        return result;
    }
}

export async function createList(listData: Partial<List>): Promise<Result> {
    let result: Result;

    listData.docMeta = {
        updateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
        lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
        lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
    };

    try {
        const listsCollection = firestore.collection('lists');
        const res = await listsCollection.add(listData);

        result = {
            success: true,
            result: res.id
        };
        return result;

    } catch (e) {
        result = { success: false, error: e };
        console.log(e);
        analyticsService().logError(e);
        return result;
    }
}

export async function deleteList(listId: string): Promise<Result> {
    try {
        const listRef = firestore.collection('lists').doc(listId);
        const res = await listRef.delete();

        return {
            success: true,
            result: res,
        }
    } catch (e) {
        console.log(e);
        analyticsService().logError(e);
        return { success: false, error: e };
    }
}

export async function updateList<const T extends Partial<DeepNullable<List>> & { id: string }>(list: T & DeepNoUndefined<T>): Promise<Result> {
    try {
        const listRef = firestore.collection('lists').doc(list.id);

        // NOTE: use update here, so that we overwrite any known fields,
        // specifically the talent object, otherwise we'll preserve talent that has been removed
        const res = await listRef.set({
            ...list,
            docMeta: {
                updateTime: firebase.firestore.FieldValue.serverTimestamp(),
            },
        }, { merge: true });

        return {
            success: true,
            result: res,
        }
    } catch (e) {
        console.log(e);
        analyticsService().logError(e);
        return { success: false, error: e };
    }
}

export async function deleteTalentFromList(listId: string, talentId: string) {
    try {
        const listRef = firestore.collection('lists').doc(listId);
        const res = await listRef.update({
            [`talent.${talentId}`]: firebase.firestore.FieldValue.delete(),
            'docMeta.updateTime': firebase.firestore.FieldValue.serverTimestamp(),
        });

        return {
            success: true,
            result: res,
        }
    } catch (e) {
        console.log(e);
        analyticsService().logError(e);
        return { success: false, error: e };
    }
}

export async function incrementListAddHistory(props: { talentIds: string[], managerId: string, listAddKey: keyof ListAddCountsByMonth }): Promise<Result> {
    try {
        const { talentIds, managerId, listAddKey } = props;
        const monthKey = dayjs().format('YYYY-MM');
        const incrementPromises: Promise<unknown>[] = [];

        // Increment Manager Record by total number of new creators added
        const managerIncrementUpdate: Partial<User> = {
            listAddCountsByMonth: {
                [listAddKey]: {
                    [monthKey]: firebase.firestore.FieldValue.increment(talentIds.length),
                }
            }
        };
        const managerRef = firestore.collection("users").doc(managerId);
        const managerPromise = managerRef.set(managerIncrementUpdate, { merge: true });
        incrementPromises.push(managerPromise);

        // Increment Each Talent Record by 1. FieldValue.increment does not go through createTalentUpdate.
        const talentIncrementUpdate = {
            listAddCountsByMonth: {
                [listAddKey]: {
                    [monthKey]: firebase.firestore.FieldValue.increment(1),
                }
            }
        };
        for (const talId of talentIds) {
            const talRef = firestore.collection("talent").doc(talId);
            const talPromise = talRef.set(talentIncrementUpdate, { merge: true });
            incrementPromises.push(talPromise);
        }

        // Wait for things to finish up
        await Promise.all(incrementPromises);
        return { success: true, result: null };
    } catch (e) {
        console.log(`incrementListAddHistory error`, e);
        return { success: false, error: e };
    }

}

export async function updateClient<const T extends Partial<DeepNullable<Client>> & { id: string }>(clientData: T & DeepNoUndefined<T>): Promise<Result> {
    let result: Result;

    try {
        const userRef = firestore.collection('clients').doc(clientData.id);
        const userRes = await userRef.set({
            ...clientData,
            docMeta: {
                updateTime: firebase.firestore.FieldValue.serverTimestamp(),
                lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
                lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp(),
            },
        }, { merge: true });

        result = { success: true, result: userRes };
        return result;
    } catch (e) {
        result = { success: false, error: e };
        console.log(e);
        analyticsService().logError(e);
        return result;
    }
}

export async function newCreateOrUpdateCampaign<const T extends Partial<DeepNullable<Campaign>> & { id: string }>(
    campaign: T & DeepNoUndefined<T>,
    newBookings: Booking[],
    fileList?: FileList,
): Promise<Result> {
    try {
        const batch = firestore.batch();
        let fileUrl: string | undefined;
        if (fileList) {
            const fileRes = await uploadFile(fileList);
            if (fileRes?.success) {
                fileUrl = fileRes.result;
            }
        }

        const docMeta = {
            updateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
            lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
            lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
        };

        const bookings: { [id: string]: Booking } = {};
        for (const booking of newBookings) {
            const newDocRef = firestore.collection("bookings").doc(booking.id);
            batch.set(newDocRef, { ...booking, docMeta: docMeta });
            bookings[newDocRef.id] = booking;
        }

        const campaignRef = firestore.collection("campaigns").doc(campaign.id);
        batch.set(campaignRef, {
            ...campaign,
            ...(fileUrl !== undefined ? { fileUrl } : {}),
            // Non-empty map replaces a stored null. Empty: omit, so create's bookings: null survives.
            ...(Object.keys(bookings).length ? { bookings } : {}),
            docMeta,
        }, { merge: true });

        await batch.commit();

        return {
            success: true,
            result: undefined,
        }
    } catch (e) {
        console.error(e);
        analyticsService().logError(e);

        return {
            success: false,
            error: e,
        }
    }
}

/** Settings-tab patch only. Does not inject bookings or rewrite system-owned fields. */
export async function updateCampaignSettings<const T extends Partial<DeepNullable<Campaign>>>(
    campaignId: string,
    patch: T & DeepNoUndefined<T>,
    fileList?: FileList,
): Promise<Result> {
    try {
        let fileUrl: string | undefined;
        if (fileList) {
            const fileRes = await uploadFile(fileList);
            if (fileRes?.success) {
                fileUrl = fileRes.result;
            }
        }

        const docMeta = {
            updateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
            lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
            lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
        };

        const campaignRef = firestore.collection("campaigns").doc(campaignId);
        await campaignRef.set({ ...patch, ...(fileUrl !== undefined ? { fileUrl } : {}), docMeta }, { merge: true });

        return {
            success: true,
            result: fileUrl,
        };
    } catch (e) {
        console.error(e);
        analyticsService().logError(e);

        return {
            success: false,
            error: e,
        };
    }
}

/** Status plus digestKind / digestAt for a digest-worthy pitch write. Unreject deletes both digest fields. */
export function pitchDigestPayload(action: PitchDigestAction) {
    const fields = pitchDigestFields(action);
    if ("clearDigest" in fields) {
        return {
            status: fields.status,
            digestKind: firebase.firestore.FieldValue.delete(),
            digestAt: firebase.firestore.FieldValue.delete(),
        };
    }
    return {
        status: fields.status,
        digestKind: fields.digestKind,
        digestAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
}

/**
 * Accept a pitch: one bookings doc, merge that booking into the campaign, and stamp the pitch.
 * `pitchId` on the booking keeps onWriteCampaign from sending booking-opp and receipt mail.
 * A pitch that already has bookingId returns that id and does not create another booking.
 */
export async function approvePitchAsBooking(
    campaignId: string,
    pitchId: string,
    booking: Omit<Booking, "id">,
): Promise<Result> {
    const bookingRef = firestore.collection("bookings").doc();
    const bookingId = bookingRef.id;
    const pitchRef = firestore.collection("pitches").doc(pitchId);
    const campaignRef = firestore.collection("campaigns").doc(campaignId);

    try {
        const resultId = await firestore.runTransaction(async (tx) => {
            const pitchSnap = await tx.get(pitchRef);
            if (!pitchSnap.exists) {
                throw new Error(`Pitch not found: ${pitchId}`);
            }
            const stored = pitchSnap.data() as Pitch;
            if (stored.campaignId !== campaignId) {
                throw new Error(`Pitch ${pitchId} belongs to campaign ${stored.campaignId}, not ${campaignId}`);
            }
            if (stored.bookingId) {
                return stored.bookingId;
            }
            if (stored.status !== "pitched") {
                throw new Error(`Pitch ${pitchId} cannot be approved from status ${stored.status}`);
            }

            const docMeta = {
                updateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
                lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
                lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
            };
            const bookingDoc: Booking = {
                ...booking,
                id: bookingId,
                pitchId,
                price: stored.price,
                talentId: stored.talentId,
                campaignId: stored.campaignId,
            };
            tx.set(bookingRef, { ...bookingDoc, docMeta });
            tx.set(campaignRef, {
                bookings: { [bookingId]: bookingDoc },
                docMeta,
            }, { merge: true });
            tx.set(pitchRef, {
                ...pitchDigestPayload("accepted"),
                bookingId,
                docMeta,
            }, { merge: true });
            return bookingId;
        });

        return {
            success: true,
            result: resultId,
        };
    } catch (e) {
        console.error(e);
        analyticsService().logError(e);

        return {
            success: false,
            error: e,
        };
    }
}

/**
 * Open or close a campaign for pitches.
 * Always stamps pitchingOpenChangedAt; never clears it.
 */
export async function setCampaignPitchingOpen(campaignId: string, open: boolean): Promise<Result> {
    try {
        const campaignRef = firestore.collection("campaigns").doc(campaignId);
        const next = { pitchingOpen: open, pitchingOpenChangedAt: new Date() };
        await campaignRef.set({
            ...next,
            docMeta: {
                updateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
                lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
                lastKnownUpdateTime:
                    firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
            },
        }, { merge: true });

        analyticsService().logEvent("Manager Updated Doc", {
            collection: "campaigns",
            docId: campaignId,
            value: next,
            note: "",
        });
        return { success: true, result: next };
    } catch (e) {
        console.error(e);
        analyticsService().logError(e);
        return { success: false, error: e };
    }
}

/** Reject these pitches in one batch. A failed commit leaves every pitch unchanged. */
export async function rejectPitches(pitchIds: string[]): Promise<Result> {
    if (pitchIds.length === 0) {
        return { success: true, result: null };
    }

    try {
        const batch = firestore.batch();
        const docMeta = {
            updateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
            lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
            lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
        };
        const patch = pitchDigestPayload("rejected");

        for (const pitchId of pitchIds) {
            batch.set(firestore.collection("pitches").doc(pitchId), { ...patch, docMeta }, { merge: true });
        }
        await batch.commit();

        for (const pitchId of pitchIds) {
            analyticsService().logEvent("Manager Updated Doc", {
                collection: "pitches",
                docId: pitchId,
                value: patch,
                note: "",
            });
        }
        return { success: true, result: null };
    } catch (e) {
        console.error(e);
        analyticsService().logError(e);
        return { success: false, error: e };
    }
}

/** Save price and notes. Stamps digest updated and sets status pitched (needs-info → pitched).
 * Edit dialogs take the live streamed pitch, so a concurrent Accept / Reject flips `canEditPitch` and Save is a no-op. 
 * 
 * The leftover is a reviewer Accept / Reject plus Save before that snapshot, which would spread `pitchDigestPayload("updated")` and write `status: "pitched"` over the new status (Accept is the painful one: the booking may already exist). 
 * That needs two people on the same pitch and a click in the snapshot gap — unlikely, so we do not add a transaction for it. Withdraw is the same people as Edit, so it is not this race.
 */
export async function updatePitchFromEdit(
    pitchId: string,
    price: number,
    notes: Record<string, unknown> | null,
): Promise<Result> {
    return updateDoc("pitches", pitchId, {
        price,
        notes: notes as FirestoreData | null,
        ...pitchDigestPayload("updated"),
    });
}

export async function withdrawPitch(pitchId: string): Promise<Result> {
    return updateDoc("pitches", pitchId, pitchDigestPayload("withdrawn"));
}

export async function resubmitPitch(pitchId: string): Promise<Result> {
    return updateDoc("pitches", pitchId, pitchDigestPayload("updated"));
}

export async function rejectPitch(pitchId: string): Promise<Result> {
    return updateDoc("pitches", pitchId, pitchDigestPayload("rejected"));
}

export async function unrejectPitch(pitchId: string): Promise<Result> {
    return updateDoc("pitches", pitchId, pitchDigestPayload("clearDigest"));
}

export async function updateUser<const T extends Partial<DeepNullable<User>> & { id: string }>(userData: T & DeepNoUndefined<T>): Promise<Result> {
    let result: Result;

    try {
        const userRef = firestore.collection('users').doc(userData.id); // firebase.auth().currentUser?.uid
        const userRes = await userRef.set({
            ...userData,
            docMeta: {
                ...("docMeta" in userData && userData.docMeta && typeof userData.docMeta === "object" ? userData.docMeta : {}),
                updateTime: firebase.firestore.FieldValue.serverTimestamp(),
                lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
                lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp(),
            },
        }, { merge: true });

        result = { success: true, result: userRes };
        return result;
    } catch (e) {
        result = { success: false, error: e };
        console.log(e);
        analyticsService().logError(e);
        return result;
    }
}

export async function updateTalent<const T extends Partial<DeepNullable<Talent>> & { id: string }>(talentData: T & DeepNoUndefined<T>): Promise<Result> {
    let result: Result;

    try {
        const talentRef = firestore.collection('talent').doc(talentData.id);
        const talentRes = await talentRef.set({
            ...talentData,
            // docMeta: { ...talentData.docMeta, updateTime: firebase.firestore.FieldValue.serverTimestamp() },
            docMeta: {
                ...("docMeta" in talentData && talentData.docMeta && typeof talentData.docMeta === "object" ? talentData.docMeta : {}),
                updateTime: firebase.firestore.FieldValue.serverTimestamp(),
                lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
                lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp(),
            },
        }, { merge: true });

        result = { success: true, result: talentRes };
        return result;
    } catch (e) {
        result = { success: false, error: e };
        console.log(e);
        analyticsService().logError(e);
        return result;
    }
}

export async function updateUserLastSignInTime(uid: string) {
    await updateUser(createUserUpdate({
        id: uid,
        docMeta: {
            lastSignInTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
        },
    }));
    return;
}

export type PitchSubmitItem = {
    talentId: string
    talentName: string
    price: number
    notes?: Record<string, unknown>
}

/** Pre-write uniqueness query, then batch-set pitches and increment campaign pitchCount. */
export async function submitPitchesBatch(args: {
    campaignId: string
    managerId: string
    managerEmail?: string
    items: PitchSubmitItem[]
}): Promise<{ createdIds: string[]; skippedTalentIds: string[] }> {
    const skippedTalentIds: string[] = []
    const toCreate: Array<PitchSubmitItem & { id: string }> = []

    await Promise.all(
        args.items.map(async (item) => {
            const snap = await firestore
                .collection("pitches")
                .where("campaignId", "==", args.campaignId)
                .where("talentId", "==", item.talentId)
                .limit(1)
                .get()
            logDatabaseReads("submitPitchesBatch uniqueness", snap.docs.length)
            if (!snap.empty) {
                skippedTalentIds.push(item.talentId)
                return
            }
            toCreate.push({ ...item, id: generateNewDocId("pitches") })
        }),
    )

    if (!toCreate.length) {
        return { createdIds: [], skippedTalentIds }
    }

    const batch = firestore.batch()
    const docMeta = {
        updateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
        lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
        lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
    }

    for (const item of toCreate) {
        const pitch: Pitch = {
            id: item.id,
            campaignId: args.campaignId,
            talentId: item.talentId,
            talentName: item.talentName,
            managerId: args.managerId,
            managerEmail: args.managerEmail,
            price: item.price,
            status: "pitched",
            docMeta,
        }
        if (item.notes) {
            pitch.notes = item.notes
        }
        batch.set(firestore.collection("pitches").doc(item.id), { ...pitch, ...pitchDigestPayload("new") })
    }

    batch.set(
        firestore.collection("campaigns").doc(args.campaignId),
        {
            pitchCount: firebase.firestore.FieldValue.increment(toCreate.length),
            docMeta,
        },
        { merge: true },
    )

    await batch.commit()
    return { createdIds: toCreate.map((item) => item.id), skippedTalentIds }
}

/**
 * Field editor: one field’s new value. Leaf empty (`undefined` / `""`) becomes `null`.
 * `0` / `false` / arrays / maps stay. Does not turn `[]` into `null`.
 * @see .cursor/rules/firestore-writes.mdc
 * @see docs/firestore-writes.md
 */
export async function updateDocField(collection: string, docId: string, fieldName: string, newVal: FirestoreField | undefined) {
    let result: Result;
    const value = newVal === undefined || newVal === "" ? null : newVal;

    // console.log(collection, docId, fieldName, newVal);

    try {
        const docRef = firestore.collection(collection).doc(docId);
        const res = await docRef.set({
            [fieldName]: value,
            docMeta: {
                updateTime: firebase.firestore.FieldValue.serverTimestamp(),
                lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
                lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp(),
            },
        }, { merge: true });

        result = { success: true, result: res };
        analyticsService().logEvent("Manager Updated Field", { collection, docId, fieldName, value });
        return result;
    } catch (e) {
        result = { success: false, error: e };
        console.log(e);
        analyticsService().logError(e);
        return result;
    }
}

export async function updateDoc(collection: string, docId: string, newVal: FirestoreData, note: string = "") {
    let result: Result;

    try {
        const docRef = firestore.collection(collection).doc(docId);
        const res = await docRef.set({
            // [fieldName]: newVal || null,
            ...newVal,
            docMeta: {
                updateTime: firebase.firestore.FieldValue.serverTimestamp(),
                lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
                lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp(),
            },
        }, { merge: true });

        result = { success: true, result: res };
        analyticsService().logEvent("Manager Updated Doc", { collection, docId, value: newVal, note });
        return result;
    } catch (e) {
        result = { success: false, error: e };
        console.log(e);
        analyticsService().logError(e);
        return result;
    }
}

// Seperate from updateDocField because of nesting...
export async function updateUserRole(userId: string, role: string, newVal: boolean) {
    let result: Result;

    console.log(userId, role, newVal);

    try {
        const docRef = firestore.collection("users").doc(userId);
        const res = await docRef.set({
            roles: {
                [role]: newVal || false,
            },
            docMeta: {
                updateTime: firebase.firestore.FieldValue.serverTimestamp(),
                lastKnownUpdateUser: firebase.auth().currentUser?.uid || null,
                lastKnownUpdateTime: firebase.firestore.FieldValue.serverTimestamp(),
            },
        }, { merge: true });

        result = { success: true, result: res };
        console.log(`result`, result);
        analyticsService().logEvent("Admin Updated Role", { userId, role, value: newVal });
    } catch (e) {
        result = { success: false, error: e };
        console.log(e);
        analyticsService().logError(e);
        return result;
    }
}
